import { Award, Heart, Recycle, ThumbsUp } from "lucide-react";
import { FadeUp } from "./Reveal";
import { BRAND } from "@/lib/brand";

const POINTS = [
  {
    Icon: Award,
    title: "25 years in the field",
    desc: "Over two decades running bobcats, hauling and shaping yards across South Texas. We know the work — and the shortcuts that don't work.",
  },
  {
    Icon: Heart,
    title: "East Side, San Antonio",
    desc: "Born and raised on the East Side. When you call El Monos, you talk to an owner — not a call center in another state.",
  },
  {
    Icon: Recycle,
    title: "We leave it better",
    desc: "We don't just do the job — we sweep, level, and leave every site cleaner than we found it. That's the standard.",
  },
  {
    Icon: ThumbsUp,
    title: "No hidden fees",
    desc: "The price we quote is the price you pay. No fuel charges, no surprise add-ons, no runaround.",
  },
];

export default function WhyUs() {
  return (
    <section
      id="why"
      data-testid="why-us-section"
      className="py-28 md:py-40 bg-[#FFFFFF] text-[#111613]"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <FadeUp className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="chapter-line text-[#0E2A1A] mb-6">
              Ch. 04 · Manifesto
            </div>
            <h2
              className="font-display text-[#0E2A1A] text-6xl sm:text-7xl md:text-8xl leading-[0.92] tracking-[-0.03em] mb-8"
              style={{ fontWeight: 400 }}
            >
              We work
              <br />
              like it's{" "}
              <span className="italic text-[#DA7B3C]">our own</span>
              <br />
              backyard.
            </h2>
            <p className="text-lg md:text-xl text-[#111613]/75 leading-relaxed mb-10 max-w-md">
              {"Half the time it is a neighbor's — and we've got to see them at H-E-B on Saturday."}
            </p>

            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div className="p-6 border border-[#111613]/15 bg-[#FFFFFF]/60">
                <div
                  className="font-display text-5xl md:text-6xl text-[#0E2A1A] leading-none"
                  style={{ fontWeight: 400 }}
                >
                  {BRAND.yearsExperience}
                  <span className="text-[#DA7B3C]">+</span>
                </div>
                <div className="mt-3 font-mono-sm text-[#6B7A6E]">
                  Years operating
                </div>
              </div>
              <div className="p-6 border border-[#111613]/15 bg-[#FFFFFF]/60">
                <div
                  className="font-display text-5xl md:text-6xl text-[#0E2A1A] leading-none"
                  style={{ fontWeight: 400 }}
                >
                  100<span className="text-[#DA7B3C]">%</span>
                </div>
                <div className="mt-3 font-mono-sm text-[#6B7A6E]">
                  Family-owned
                </div>
              </div>
            </div>
          </FadeUp>

          <div className="lg:col-span-7 space-y-4">
            {POINTS.map(({ Icon, title, desc }, idx) => (
              <FadeUp key={title} delay={idx * 0.08}>
                <div
                  data-testid={`why-card-${idx}`}
                  className="group relative p-8 md:p-10 border border-[#111613]/15 bg-[#FFFFFF]/70 transition-all duration-500 hover:border-[#0E2A1A] hover:bg-white"
                >
                  <div className="flex items-start gap-6">
                    <div className="shrink-0">
                      <div
                        className="font-display text-4xl md:text-5xl text-[#DA7B3C]/80 leading-none italic"
                        style={{ fontWeight: 400 }}
                      >
                        {String(idx + 1).padStart(2, "0")}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <Icon
                          className="w-4 h-4 text-[#0E2A1A] group-hover:text-[#DA7B3C] transition-colors"
                          strokeWidth={1.75}
                        />
                        <span className="font-mono-sm text-[#6B7A6E]">
                          Principle {idx + 1}
                        </span>
                      </div>
                      <h3
                        className="font-display text-3xl md:text-4xl tracking-[-0.025em] mb-3 leading-tight text-[#0E2A1A]"
                        style={{ fontWeight: 400 }}
                      >
                        {title}
                      </h3>
                      <p className="text-base md:text-lg text-[#111613]/70 leading-relaxed max-w-xl">
                        {desc}
                      </p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
